import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Menu,
  MenuButton,
  Portal,
  Text
} from '@chakra-ui/react'
import { EditorContent } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PersonaType } from '@/modules/auth/domain/models/Persona'
import { GroupUsersByFeedGroup } from '@/shared/services/api/access-profile/list-group-user-profiles'
import UserGroupMembersMenuList from '@/therapist/pages/feed/timeline/PostForm/ImportantToGroup/UserGroupMembersMenuList'
import { useUserGroupListQuery } from '@/ui/hooks/queries/useUserGroupListQuery'

import { markdownDeserializer } from './Editor/markdownDeserializer'
import useMarkdownEditor from './Editor/useMarkdownEditor'
import { MentionSuggestionModel } from './Mention/MentionSuggestionModel'
import Menubar from './Menubar'
import { countMarkdownText } from './utils/countMarkdownText'
import { getMarkdownMentionStorage } from './utils/getMarkdownMentionStorage'
import { truncateMarkdown } from './utils/truncateMarkdown'

export default function Markdown<T = string>({
  isReadonly,
  name,
  value,
  padding,
  onChange,
  onBlur,
  mentionItems,
  mentionQuery,
  getMentionSelected,
  mentionResetCheck,
  mentionKey = '',
  limit,
  IsComment,
  IsCreatePost,
  onFocused,
  groupId,
  placeholder,
  sendOnEnter,
  onSendMessage,
  bottomMenu = false,
  bottomBorder,
  ...props
}: {
  isReadonly?: boolean
  IsCreatePost?: boolean
  IsComment?: boolean
  name?: string
  value: string
  padding?: string
  onChange?: (value?: string) => void
  onBlur?: () => void
  mentionItems?: {
    id: string
    personaType: PersonaType | 'GROUP' | 'ACCESS_PROFILE'
    avatarName: string
    avatarUrl?: string
    fullname: string
  }[]
  mentionQuery?: (query: string) => void
  getMentionSelected?: (selected: { id: string; name: string }) => void
  mentionResetCheck?: T
  mentionKey?: string
  limit?: number
  onFocused?: () => void
  groupId?: string
  placeholder?: string
  sendOnEnter?: boolean
  onSendMessage?: () => void
  bottomBorder?: boolean
  bottomMenu?: boolean
} & ButtonProps) {
  const { t } = useTranslation()

  const [mentionResetState, setMentionResetState] = useState(mentionResetCheck)
  const [showMorePost, setShowMorePost] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [clickedUserGroup, setClickedUserGroup] =
    useState<GroupUsersByFeedGroup>()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const editor = useMarkdownEditor({
    mentionQuery,
    onChange,
    isReadonly,
    mentionKey,
    limit
  })
  const userGroupListQuery = useUserGroupListQuery(groupId || '', {
    enabled: false
  })

  useEffect(() => {
    if (
      !editor ||
      (!editor.state.doc.textContent && !value) ||
      (editor.state.doc.textContent && value)
    )
      return

    if (value.length === 2 && (value === '* ' || value === '- ')) return

    editor.commands.setContent(markdownDeserializer(editor.schema, value))
  }, [editor, value])

  useEffect(() => {
    if (!editor || !value || !isReadonly) return

    const content = truncateMarkdown(editor.getJSON(), 100)

    if (
      limit !== undefined &&
      content &&
      countMarkdownText(editor.getJSON()) > limit &&
      !showMorePost
    ) {
      editor.commands.setContent(content.content)
    } else {
      editor.commands.setContent(markdownDeserializer(editor.schema, value))
    }
  }, [editor, value, limit, isReadonly, showMorePost])

  useEffect(() => {
    const handleClick = async (e: MouseEvent) => {
      const target = e.target as HTMLElement

      if (target.dataset.type === 'access-profile') {
        e.preventDefault()
        e.stopPropagation()

        const id = target.dataset.id
        if (!id) return

        const getData = async () => {
          if (userGroupListQuery.data && userGroupListQuery.data.length > 0) {
            return userGroupListQuery.data
          }

          const result = await userGroupListQuery.refetch()
          return result.data ?? []
        }

        const data = await getData()
        const group = data.find((g) => g.id === Number(id))

        if (group) {
          setClickedUserGroup(group)
          setAnchorEl(target)
        }
      } else {
        setClickedUserGroup(undefined)
        setAnchorEl(null)
      }
    }

    const el = markdownEditorRef.current
    if (!el) return

    el.addEventListener('click', handleClick)
    return () => el.removeEventListener('click', handleClick)
  }, [userGroupListQuery.refetch])

  useEffect(() => {
    if (!editor || mentionItems === undefined) return
    const storage = getMarkdownMentionStorage(editor, mentionKey)
    storage.suggestions = mentionItems.map(
      ({ id, avatarUrl, fullname, personaType, avatarName }) =>
        `${id},${fullname},${personaType},${avatarName},${avatarUrl}` satisfies MentionSuggestionModel
    )
  }, [editor, mentionItems])

  useEffect(() => {
    if (!editor || !getMentionSelected) return
    const selected = getMarkdownMentionStorage(editor, mentionKey).selected
    if (!selected) return
    getMentionSelected(selected)
  }, [
    editor,
    editor ? getMarkdownMentionStorage(editor, mentionKey).selected : null
  ])

  useEffect(() => {
    if (!editor || mentionResetCheck === mentionResetState) return
    setMentionResetState(mentionResetCheck)
    const storage = getMarkdownMentionStorage(editor, mentionKey)
    storage.suggestions = []
    storage.selected = undefined
  }, [editor, mentionResetCheck])

  useEffect(() => {
    return () => {
      if (!editor) return
      const storage = getMarkdownMentionStorage(editor, mentionKey)
      storage.selected = undefined
    }
  }, [editor])

  const markdownEditorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isFocused && editor) {
      setTimeout(() => {
        editor.commands.focus()
      }, 0)
    }
  }, [isFocused, editor])

  return (
    <Flex
      ref={markdownEditorRef}
      className="editor"
      flexDirection={'column'}
      wordBreak={'break-word'}
      w={'full'}
      flex={'1'}
      maxH={IsComment ? '150px' : undefined}
      p={IsComment ? 1 : undefined}
      css={`
        .tiptap {
          padding: ${(padding ?? !isReadonly) ? '1rem' : ''};
          ${isReadonly || IsComment || bottomMenu
            ? ''
            : 'border-left: solid 1px #d8d8d8; border-right: solid 1px #d8d8d8;'}
          ${bottomBorder ? 'border-bottom: solid 1px #d8d8d8;' : ''}
          outline-color: #d8d8d8;
          ${IsCreatePost && !IsComment
            ? 'height: 300px; overflow-y: auto;'
            : ''}

          > * + * {
            margin-top: 0.75em;
          }

          a {
            color: #2f81f7;
          }

          span.mention-access-profile {
            text-decoration: 'none';
          }

          ul,
          ol {
            padding: 0 1rem;
          }

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            margin-top: 24px;
            margin-bottom: 16px;
            font-weight: 600;
            line-height: 1.25;
          }

          h1 {
            font-size: 2em;
          }

          h2 {
            font-size: 1.5em;
          }

          h3 {
            font-size: 1.25em;
          }

          h4 {
            font-size: 1em;
          }

          h5 {
            font-size: 0.875em;
          }

          h6 {
            font-size: 0.85em;
          }

          code {
            background-color: rgba(#616161, 0.1);
            color: #616161;
          }

          pre {
            background: #0d0d0d;
            border-radius: 0.5rem;
            color: #fff;
            font-family: 'JetBrainsMono', monospace;
            padding: 0.75rem 1rem;

            code {
              background: none;
              color: inherit;
              font-size: 0.8rem;
              padding: 0;
            }
          }

          mark {
            background-color: #faf594;
          }

          img {
            height: auto;
            max-width: 100%;
          }

          hr {
            margin: 1rem 0;
          }

          blockquote {
            border-left: 2px solid rgba(#0d0d0d, 0.1);
            padding-left: 1rem;
          }

          hr {
            border: none;
            border-top: 2px solid rgba(#0d0d0d, 0.1);
            margin: 2rem 0;
          }

          ul[data-type='taskList'] {
            list-style: none;
            padding: 0;

            li {
              align-items: center;
              display: flex;

              > label {
                flex: 0 0 auto;
                margin-right: 0.5rem;
                user-select: none;
              }

              > div {
                flex: 1 1 auto;
              }
            }
          }
        }
        p.is-editor-empty:first-of-type::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        [contenteditable]:focus {
          outline: none;
        }

        .editor {
          &__header {
            align-items: center;
            ${isReadonly || IsComment ? '' : 'border: solid 1px #d8d8d8;'}
            ${bottomMenu ? 'border-bottom: none;' : ''}
            border-top-left-radius: 0.25rem;
            border-top-right-radius: 0.25rem;
            display: flex;
            flex: 1;
            flex-wrap: wrap;
            padding: 0.25rem;
          }

          &__content {
            flex: 1 1 auto;
            overflow-x: hidden;
            overflow-y: auto;
            border: none;
            -webkit-overflow-scrolling: touch;
          }

          /* Some information about the status */
          &__status {
            align-items: center;
            border-radius: 5px;
            display: flex;

            &::before {
              background: rgba(#0d0d0d, 0.5);
              border-radius: 50%;
              content: ' ';
              display: inline-block;
              flex: 0 0 auto;
              height: 0.5rem;
              margin-right: 0.5rem;
              width: 0.5rem;
            }

            &--connecting::before {
              background: #616161;
            }

            &--connected::before {
              background: #b9f18d;
            }
          }

          &__name {
            button {
              background: none;
              border: none;
              border-radius: 0.4rem;
              color: #0d0d0d;
              font: inherit;
              font-size: 12px;
              font-weight: 600;
              padding: 0.25rem 0.5rem;

              &:hover {
                background-color: #0d0d0d;
                color: #fff;
              }
            }
          }
        }

        .menu-item {
          background-color: transparent;
          border: none;
          border-radius: 0.4rem;
          color: var(--chakra-colors-custom-blue-default);
          cursor: pointer;
          height: 1.75rem;
          margin-right: 0.25rem;
          padding: 0.25rem;
          width: 1.75rem;

          svg {
            fill: currentColor;
            height: 100%;
            width: 100%;
          }

          &:hover {
            background-color: #f3f3f3;
          }

          &.is-active {
            background-color: #d8d8d8;
          }

          &.is-disabled {
            background-color: #f0f0f0;
            cursor: not-allowed;
          }
        }
      `}
    >
      {clickedUserGroup && anchorEl && (
        <Portal>
          <Menu
            isLazy
            isOpen
            onClose={() => {
              setClickedUserGroup(undefined)
              setAnchorEl(null)
            }}
          >
            <Box
              as={MenuButton}
              position="absolute"
              top={anchorEl.getBoundingClientRect().top + window.scrollY + 8}
              left={anchorEl.getBoundingClientRect().left + window.scrollX}
              height={1}
              width={1}
              opacity={0}
              pointerEvents="none"
            />
            <UserGroupMembersMenuList
              userGroup={clickedUserGroup}
              onClose={() => {
                setClickedUserGroup(undefined)
                setAnchorEl(null)
              }}
            />
          </Menu>
        </Portal>
      )}
      {!isFocused && IsComment && (
        <Flex
          onClick={() => {
            setIsFocused(true)

            if (!onFocused) return
            onFocused()
          }}
          align={'center'}
          w={'full'}
          h={'full'}
          px={4}
          cursor={'text'}
        >
          <Text color={'custom.gray.300'} fontWeight={400} fontSize={'14px'}>
            {placeholder ?? t('therapists.groups.writeAComment')}
          </Text>
        </Flex>
      )}
      {!bottomMenu &&
        (!IsComment || (IsComment && isFocused)) &&
        !isReadonly &&
        editor && (
          <Flex justify={'space-between'} mb={IsComment ? 1 : 0}>
            <Menubar editor={editor} markdownEditorRef={markdownEditorRef} />
            {IsComment && (
              <Button
                colorScheme="blue"
                variant="solid"
                px={3}
                borderRadius={'full'}
                {...props}
              >
                {t('therapists.groups.comment')}
              </Button>
            )}
          </Flex>
        )}
      {(!IsComment || (IsComment && isFocused)) && (
        <>
          <EditorContent
            name={name}
            onBlur={onBlur}
            editor={editor}
            placeholder={t('publish')}
            className="editor__content"
            onKeyDown={(e) => {
              if (
                sendOnEnter &&
                e.key === 'Enter' &&
                !e.shiftKey &&
                !e.ctrlKey &&
                !e.metaKey
              ) {
                e.preventDefault()
                if (onSendMessage) {
                  onSendMessage()
                  editor.commands.setContent(
                    markdownDeserializer(editor.schema, '')
                  )
                }
                return
              } else if (
                (e.ctrlKey || e.metaKey || e.shiftKey) &&
                e.key === 'Enter'
              ) {
                e.preventDefault()
                editor?.commands.keyboardShortcut('Enter')
              }
            }}
          />
          {bottomMenu &&
            (!IsComment || (IsComment && isFocused)) &&
            !isReadonly &&
            editor && (
              <Flex justify={'space-between'} mb={IsComment ? 1 : 0}>
                <Menubar
                  editor={editor}
                  markdownEditorRef={markdownEditorRef}
                />
                {IsComment && (
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    px={3}
                    borderRadius={'full'}
                    {...props}
                  >
                    {t('therapists.groups.comment')}
                  </Button>
                )}
              </Flex>
            )}
          {limit && !isReadonly && (
            <Flex py={1} justifyContent={'end'}>
              <Text fontSize={'12px'} color={'custom.gray.300'}>
                {editor && editor.getText().length} / {limit}
              </Text>
            </Flex>
          )}
          {isReadonly &&
            limit !== undefined &&
            editor &&
            countMarkdownText(markdownDeserializer(editor.schema, value)) >
              limit && (
              <Text
                fontSize={'14px'}
                onClick={() => {
                  setShowMorePost((prev) => !prev)
                }}
                textAlign={'right'}
                _hover={{
                  textDecoration: 'underline',
                  color: '#2196F3',
                  cursor: 'pointer'
                }}
              >
                {showMorePost ? t('seeLess') : `${t('seeMore')}`}
              </Text>
            )}
        </>
      )}
    </Flex>
  )
}
